// src/services/adminPersonService.ts

import apiClient from "./apiClient";
import { PersonModel, PersonPageResponse } from "../models/PersonModel";
import { PageResponse } from "../models/PaginationModel";
import { CountryCountModel } from "../models/CountryCountModel";

const ADMIN_URL  = "/api/admin/people";
const PUBLIC_URL = "/api/people";

export interface AdminPersonFilters {
  search: string;
  nationality: string;  // "" = all, "TR" / "BR" / etc.
  birthCountry: string; // "" = all; birth country code, "TR" / "BR" / etc.
  active: "" | "true" | "false";
  clubId: number | "";        // "" = all; numeric ID = filter by career club (any stint)
  currentClubId: number | ""; // "" = all; numeric ID = filter by current (active) club
  locked?: "" | "true" | "false";
  sortBy: string;
  sortDir: "asc" | "desc";
}

/**
 * Get paginated, filtered list of people — admin panel only.
 * Uses the /api/admin/people endpoint which requires authentication.
 */
export const getAdminPeople = async (
  page: number,
  pageSize: number,
  filters: AdminPersonFilters
): Promise<PersonPageResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(pageSize),
    sortBy: filters.sortBy || "lastName",
    sortDir: filters.sortDir || "asc",
  });

  if (filters.search.trim())  params.set("search",      filters.search.trim());
  if (filters.nationality)    params.set("nationality",  filters.nationality);
  if (filters.birthCountry)   params.set("birthCountry", filters.birthCountry);
  if (filters.active !== "")  params.set("active",       filters.active);
  if (filters.clubId !== "")  params.set("clubId",       String(filters.clubId));
  if (filters.currentClubId !== "") params.set("currentClubId", String(filters.currentClubId));
  if (filters.locked)         params.set("locked",       filters.locked);

  const response = await apiClient.get<PageResponse<PersonModel>>(`${ADMIN_URL}?${params.toString()}`);

  return {
    content:     response.content ?? [],
    totalPages:  response.totalPages ?? 1,
    totalItems:  response.totalItems ?? response.content?.length ?? 0,
    currentPage: page,
  };
};

/**
 * Fetch a single person by numeric ID — used by admin panel.
 */
export const getAdminPersonById = (id: number): Promise<PersonModel> =>
  apiClient.get<PersonModel>(`${ADMIN_URL}/${id}`);

/**
 * Fetch a single person by numeric ID — public endpoint, no auth required.
 */
export const getPublicPersonById = (id: number): Promise<PersonModel> =>
  apiClient.get<PersonModel>(`${PUBLIC_URL}/${id}`);

/**
 * Fetch a single person by URL slug — public endpoint, no auth required.
 * Used by the /kisi/{slug} frontend route.
 *
 * @param slug e.g. "fatih-terim", "fred"
 */
export const getPublicPersonBySlug = (slug: string): Promise<PersonModel> =>
  apiClient.get<PersonModel>(`${PUBLIC_URL}/by-slug/${slug}`);

/**
 * Lightweight fetch for the left-pane people list.
 * Uses the public /api/people endpoint — no auth required, active people only.
 * sortBy: "recentEntryCount" (default — most-active first) | "lastName" (A-Z)
 * country: optional nationality code (left-pane country filter), null/"" = all
 */
export const getPeopleForPane = async (
  page: number = 0,
  size: number = 20,
  search: string = "",
  sortBy: "lastEntryAt" | "recentEntryCount" = "lastEntryAt",
  country: string | null = null,
  // Viewer preference. Forwarded to the API so the badge counts (and the
  // "most active" ordering) exclude rookie-authored entries when the viewer
  // has them hidden — matches getAllTopics/getAllMatches so the "Bugünkü
  // Girişler" mechanism is identical across every left-pane domain.
  showRookieEntries: boolean = true
): Promise<PersonPageResponse> => {
  const sortDir = "desc"; // both sorts are descending (most recent / most active first)

  const params = new URLSearchParams({
    page:    String(page),
    size:    String(size),
    sortBy,
    sortDir,
    showRookieEntries: String(showRookieEntries),
  });

  if (search.trim()) params.set("search", search.trim());
  if (country)       params.set("country", country);

  const response = await apiClient.get<PageResponse<PersonModel>>(`${PUBLIC_URL}?${params.toString()}`);

  return {
    content:     response.content ?? [],
    totalPages:  response.totalPages ?? 1,
    totalItems:  response.totalItems ?? response.content?.length ?? 0,
    currentPage: page,
  };
};

/**
 * Distinct nationality codes of active people with per-country counts —
 * feeds the left-pane country filter dropdown. Public endpoint, no auth.
 */
export const getPersonCountries = (): Promise<CountryCountModel[]> =>
  apiClient.get<CountryCountModel[]>(`${PUBLIC_URL}/countries`);

/** Lock or unlock a person topic — blocks/allows new entry submissions (ADMIN or MODERATOR) */
export const setPersonLocked = (id: number, locked: boolean, lockReason?: string, lockReasonVisible?: boolean): Promise<PersonModel> =>
  apiClient.patch<PersonModel>(`/api/admin/people/${id}/lock`, { locked, lockReason, lockReasonVisible });